import React, { Fragment, useEffect, useState } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import { Button, FormGroup, Input } from 'reactstrap';
import { useTrainingProviderUIContext } from '../TrainingProviderUIProvider';
import TrainingProvidersTableColumns, {
  TrainingProvidersColumns,
} from './TrainingProvidersColumns';
import { TrainingProvidersTableSearchBar } from './TrainingProvidersTableSearchBar';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  getTrainingProviderList,
  setCadsAITrainingFilters,
  toggleCheckedItemById,
} from '../../redux/actions';
import SanitizeHTML from '@/shared/components/SanitizeHTML';
import EyeIcon from 'mdi-react/EyeIcon';
import PencilIcon from 'mdi-react/PencilIcon';
import LoadingIcon from 'mdi-react/LoadingIcon';
import { defaultPaginationProps } from '@/shared/helpers';

const TrainingProvidersTable = () => {
  const trainingProviderUIContext = useTrainingProviderUIContext();
  const dispatch = useDispatch();
  const {
    data: trainingProviders,
    error,
    filters,
    loading,
    totalRecords,
    checkedTrainingProviderIds,
  } = useSelector((state) => state.cadsAITrainingProviders, shallowEqual);
  const {
    pageSize: rowsPerPage,
    pageNumber: page,
    sortBy: orderBy,
    isSortOrderDescending,
  } = filters;

  const [columns, setColumns] = useState(0);

  useEffect(() => {
    setColumns(TrainingProvidersColumns.length);
  }, [TrainingProvidersColumns.length]);

  React.useEffect(() => {
    dispatch(getTrainingProviderList(filters));
  }, [filters]);

  const handleRequestSort = (event, property) => {
    dispatch(
      setCadsAITrainingFilters({
        sortBy: property,
        isSortOrderDescending: !isSortOrderDescending,
      }),
    );
  };

  const renderActionButtons = (id) => (
    <div className="actions">
      <Button
        className="icon mb-0"
        color="success"
        outline
        onClick={() => trainingProviderUIContext.openViewTrainingProviderDialog(id)}
      >
        <p>
          <EyeIcon /> View
        </p>
      </Button>
      <Button
        className="icon mb-0"
        color="primary"
        outline
        onClick={() => trainingProviderUIContext.openEditTrainingProviderDialog(id)}
      >
        <p>
          <PencilIcon /> Edit
        </p>
      </Button>
    </div>
  );

  const handleChangePage = (event, currentPage) => {
    dispatch(setCadsAITrainingFilters({ pageSize: rowsPerPage, pageNumber: currentPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(setCadsAITrainingFilters({ pageSize: +event.target.value, pageNumber: 1 }));
  };

  const emptyRows =
    page === Math.ceil(totalRecords / rowsPerPage) ? rowsPerPage - trainingProviders.length : 0;

  return (
    <Fragment>
      <div className="material-table__wrap">
        <TrainingProvidersTableSearchBar />
        <Table className="material-table">
          <TrainingProvidersTableColumns
            order={isSortOrderDescending ? 'desc' : 'asc'}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={rowsPerPage}
          />
          <TableBody>
            {(loading || !!error) && (
              <TableRow>
                <TableCell colSpan={columns}>
                  {loading && (
                    <div className={'text-center overflow-hidden'}>
                      <div className="panel__refresh position-relative" style={{ height: 75 }}>
                        <LoadingIcon />
                      </div>
                    </div>
                  )}
                  <div className={'text-center'}>
                    <p className={!!error ? 'm-5' : ''}>
                      {error || 'Loading Training Providers...'}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {!!!trainingProviders.length && !loading && !error && (
              <TableRow style={{ height: 120 }}>
                <TableCell colSpan={columns} align="center">
                  {filters.status === null && filters.search === ''
                    ? 'No training providers'
                    : 'No training providers with applied filters'}
                </TableCell>
              </TableRow>
            )}
            {trainingProviders.map((trainingProvider) => {
              return (
                <TableRow className="material-table" tabIndex={-1} key={trainingProvider?.id}>
                  <TableCell align="center" className="material-table__cell">
                    <FormGroup>
                      <Input
                        type="checkbox"
                        value={trainingProvider?.id}
                        defaultChecked={checkedTrainingProviderIds.includes(trainingProvider?.id)}
                        onChange={(e) => dispatch(toggleCheckedItemById(+e.target.value))}
                      />
                    </FormGroup>
                  </TableCell>
                  <TableCell className="material-table__cell" align="center">
                    <img
                      className={'module_logo_image'}
                      src={
                        trainingProvider?.logoUrl ??
                        'https://unified.dev.cads.ai/image/sample/rectangle-34.png'
                      }
                      alt={'TP Image'}
                    />
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    {trainingProvider?.title}
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    <SanitizeHTML
                      html={
                        trainingProvider?.description?.startsWith('<p>')
                          ? trainingProvider?.description
                          : `<p>${trainingProvider?.description ?? '-'}</p>`
                      }
                    />
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    {trainingProvider?.isGeneral ? 'Yes' : 'No'}
                  </TableCell>
                  <TableCell
                    align="center"
                    className="material-table__cell"
                    style={{ minWidth: 250 }}
                  >
                    {renderActionButtons(trainingProvider?.id)}
                  </TableCell>
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow style={{ height: 49 * emptyRows }}>
                <TableCell colSpan={columns} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page - 1}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        component={defaultPaginationProps.component}
        backIconButtonProps={defaultPaginationProps.backIconButton}
        nextIconButtonProps={defaultPaginationProps.nextIconButton}
        rowsPerPageOptions={defaultPaginationProps.rowsPerPage}
        className={defaultPaginationProps.className}
        dir={defaultPaginationProps.dir}
      />
    </Fragment>
  );
};

export default TrainingProvidersTable;
